class LectronProProductForm extends HTMLElement {
  connectedCallback() {
    this.productForm = document.getElementById(this.dataset.formId);
    if (!this.productForm) return;

    this.variantInput = this.productForm.querySelector('input[name="id"]');
    if (!this.variantInput) return;

    this.variantMap = this._loadVariantMap();

    this.sizeInputs = this.querySelectorAll('[data-lectron-size]');
    this.addonInputs = this.querySelectorAll('[data-lectron-addon]');
    this.anodizeToggle = this.querySelector('[data-lectron-anodize-toggle]');
    this.colorWrap = this.querySelector('[data-lectron-color-wrap]');
    this.colorInputs = this.querySelectorAll('[data-lectron-color]');
    this.errorEl = this.querySelector('[data-lectron-error]');

    this._wireEvents();
    this._suppressNativeVariantPicker();
    this._syncVariant();
  }

  _loadVariantMap() {
    const node = this.querySelector('[data-lectron-variant-map]');
    if (!node) return {};
    try {
      return JSON.parse(node.textContent);
    } catch (e) {
      console.warn('[lectron-pro-product-form] failed to parse variant map', e);
      return {};
    }
  }

  _wireEvents() {
    this.sizeInputs.forEach((el) =>
      el.addEventListener('change', () => this._syncVariant())
    );
    this.addonInputs.forEach((el) =>
      el.addEventListener('change', (evt) => {
        if (el.dataset.lectronAddon === 'anodize') this._onAnodizeToggle();
        this._syncVariant();
      })
    );
    this.productForm.addEventListener('submit', (e) => this._onSubmit(e));
  }

  _onAnodizeToggle() {
    const on = this.anodizeToggle && this.anodizeToggle.checked;
    if (this.colorWrap) {
      if (on) this.colorWrap.removeAttribute('hidden');
      else this.colorWrap.setAttribute('hidden', '');
    }
    this.colorInputs.forEach((inp) => {
      inp.disabled = !on;
      if (!on) inp.checked = false;
    });
  }

  _getSelectedSize() {
    const sel = Array.from(this.sizeInputs).find((i) => i.checked);
    return sel ? sel.value : null;
  }

  _getSelectedAddons() {
    const addons = [];
    this.addonInputs.forEach((el) => {
      if (el.checked) addons.push(el.dataset.lectronAddon);
    });
    addons.sort();
    return addons;
  }

  _syncVariant() {
    const size = this._getSelectedSize();
    if (!size) return;
    const addons = this._getSelectedAddons();
    const key = `${size}|${addons.join(',')}`;
    const targetId = this.variantMap[key];

    if (!targetId) {
      console.warn('[lectron-pro-product-form] no variant for key', key);
      return;
    }

    if (this.variantInput.value !== String(targetId)) {
      this.variantInput.value = targetId;
      this.variantInput.dispatchEvent(new Event('change', { bubbles: true }));
      this._publishVariantUpdate(targetId);
    }
  }

  async _publishVariantUpdate(variantId) {
    const sectionId = this._getSectionId();
    if (!sectionId) return;

    const url = `${window.location.pathname}?section_id=${sectionId}&variant=${variantId}`;
    try {
      const response = await fetch(url, { cache: 'no-store' });
      if (!response.ok) return;
      const text = await response.text();
      const html = new DOMParser().parseFromString(text, 'text/html');

      const section = this.closest('.shopify-section') || document.querySelector('.shopify-section');
      if (!section) return;

      section.dispatchEvent(
        new CustomEvent('variant:update', {
          bubbles: true,
          detail: {
            resource: { id: variantId },
            sourceId: this.id,
            data: { html, productId: section.dataset.productId || '' },
          },
        })
      );
    } catch (e) {
      console.warn('[lectron-pro-product-form] variant update fetch failed', e);
    }
  }

  _getSectionId() {
    const formId = this.dataset.formId;
    if (!formId) return null;
    return formId.replace('BuyButtons-ProductForm-', '');
  }

  _suppressNativeVariantPicker() {
    const native = document.querySelector('variant-selects, .variant-picker, [is="variant-selects"]');
    if (native) native.style.display = 'none';
  }

  _onSubmit(e) {
    const errors = [];
    if (!this._getSelectedSize()) errors.push('Please select a size.');

    if (this.anodizeToggle && this.anodizeToggle.checked) {
      const colorPicked = Array.from(this.colorInputs).some((i) => i.checked);
      if (!colorPicked) errors.push('Please pick an anodize color.');
    }

    if (errors.length && this.errorEl) {
      this.errorEl.textContent = errors.join(' ');
      this.errorEl.removeAttribute('hidden');
      e.preventDefault();
      e.stopImmediatePropagation();
      return false;
    }

    if (this.errorEl) {
      this.errorEl.textContent = '';
      this.errorEl.setAttribute('hidden', '');
    }

    // Mirror selections into cart note + attributes for the Flow email.
    // Fire-and-forget: don't block Horizon's AJAX add-to-cart, don't
    // re-submit the form (that was causing doubled qty).
    if (!this._cartContextWritten) {
      this._cartContextWritten = true;
      this._writeCartContext();
    }
  }

  _collectOrderInfo() {
    const product = (document.querySelector('h1') || {}).textContent || '';
    const ymm = this.querySelector('[data-lectron-ymm]')?.value || '';
    const cc = this.querySelector('[data-lectron-cc]')?.value || '';
    const notes = this.querySelector('textarea[name="properties[Notes]"]')?.value || '';
    const size = this._getSelectedSize() || '';
    const addons = this._getSelectedAddons();
    const color = Array.from(this.colorInputs).find((i) => i.checked)?.value || '';
    return { product: product.trim(), ymm, cc, size, addons, color, notes };
  }

  async _writeCartContext() {
    const info = this._collectOrderInfo();
    const addonLabels = {
      anodize: info.color ? `Anodized — ${info.color}` : 'Anodized',
      longbody: 'Long Body Carb (90mm OL & throttle cable)',
      tps: 'TPS (Throttle Position Sensor)',
    };
    const addonText = info.addons.map((k) => addonLabels[k] || k).join(', ');

    const lines = [
      info.product && `Product: ${info.product}`,
      info.size && `Carb Kit: ${info.size}`,
      info.ymm && `Year, Make, Model: ${info.ymm}`,
      info.cc && `Engine CC: ${info.cc}`,
      addonText && `Customizations: ${addonText}`,
      info.notes && `Customer Notes: ${info.notes}`,
    ].filter(Boolean);
    const note = lines.join('\n');

    const attributes = {};
    if (info.ymm) attributes['Year, Make, Model'] = info.ymm;
    if (info.cc) attributes['Engine CC'] = info.cc;
    if (info.addons.includes('anodize') && info.color) attributes['Anodized Color'] = info.color;
    if (info.addons.includes('longbody')) attributes['Long Body Carb'] = 'Yes';
    if (info.addons.includes('tps')) attributes['TPS'] = 'Yes';
    if (info.notes) attributes['Customer Notes'] = info.notes;

    try {
      await fetch('/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ note, attributes }),
      });
    } catch (err) {
      console.warn('[lectron-pro-product-form] cart context write failed', err);
    }
  }
}

if (!customElements.get('lectron-pro-product-form')) {
  customElements.define('lectron-pro-product-form', LectronProProductForm);
}
